import { useMemo } from 'react';
import { useParams } from 'react-router';
import {
  ErrorPanel,
  FullScreenOverlay,
  LoadingIndicator,
} from '@nbirl/nbi-common-ui-libs';
import { FormikHelpers } from 'formik';
import {
  isEqual as _isEqual,
  unionWith as _unionWith,
  xorWith as _xorWith,
} from 'lodash';

import { Role } from '../../shared/enum';
import { useErrorToast } from '../../shared/hooks';
import { BaseLayout } from '../../shared/layout';
import { useRootFoldersQuery } from '../resources/folders/queries';
import { UserForm, UserFormValues, UserSaveSuccessModal } from './components';
import { useEditUserMutation, useUserQuery } from './queries';
import { handleEditUserError } from './utils';

export const EditUser = () => {
  const { userId } = useParams<{ userId: string }>();
  const userQuery = useUserQuery(userId);
  const foldersQuery = useRootFoldersQuery();
  const editUserMutation = useEditUserMutation();
  const { showErrorToast } = useErrorToast();

  const [allFolders, disabledFolders] = useMemo(() => {
    const rootFolders = foldersQuery.data ?? [];
    const userFolders = userQuery.data?.folders ?? [];
    const combinedFolders = _unionWith(rootFolders, userFolders, _isEqual);

    return [combinedFolders, _xorWith(combinedFolders, rootFolders, _isEqual)];
  }, [foldersQuery.data, userQuery.data]);

  const handleSubmit = async (
    user: UserFormValues,
    { setFieldError }: FormikHelpers<UserFormValues>
  ) => {
    try {
      await editUserMutation.mutateAsync({
        userId,
        ...user,
        folderIds: user.role === Role.FullAdmin ? [] : user.folderIds,
      });
    } catch (error) {
      handleEditUserError(error, setFieldError, showErrorToast);
    }
  };

  if (userQuery.isLoading || foldersQuery.isLoading) {
    return (
      <FullScreenOverlay>
        <LoadingIndicator />
      </FullScreenOverlay>
    );
  }

  if (userQuery.isError || foldersQuery.isError) {
    return (
      <FullScreenOverlay>
        <ErrorPanel error={userQuery.error || foldersQuery.error} />
      </FullScreenOverlay>
    );
  }

  return (
    <BaseLayout>
      <UserForm
        onSubmit={handleSubmit}
        user={userQuery.data}
        folders={allFolders}
        disabledFolders={disabledFolders}
      />
      {editUserMutation.data && (
        <UserSaveSuccessModal
          heading="User Edited"
          user={editUserMutation.data}
        />
      )}
    </BaseLayout>
  );
};
